import React, { useState } from "react";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calculator, Info } from "lucide-react";

// Densities (kg/dm³) → 0.00785 constant for steel
const DENSITY = 7.85;

const num = (v) => Number(v || 0);
const round = (v, d = 2) => Math.round((v + Number.EPSILON) * 10 ** d) / 10 ** d;

// Formulas (Turkish steel/iron industry standards) - all inputs in mm
const calc = {
  // Yuvarlak Demir: kg = Ø² × 0.006165 × L(mm) / 1000
  yuvarlak: (o, L, adet) => 0.006165 * o * o * num(L) / 1000 * num(adet || 1),
  // Kare Demir: kg = a² × 0.00785 × L(mm) / 1000
  kare: (a, L, adet) => 0.00785 * a * a * num(L) / 1000 * num(adet || 1),
  // Lama: kg = a × b × 0.00785 × L(mm) / 1000
  lama: (a, b, L, adet) => 0.00785 * a * b * num(L) / 1000 * num(adet || 1),
  // Sac: kg = L(mm) × W(mm) × t(mm) × 7.85 / 1,000,000
  sac: (L, W, t, adet) => num(L) * num(W) * num(t) * DENSITY / 1000000 * num(adet || 1),
  // Boru: kg = (D - t) × t × 0.02466 × L(mm) / 1000
  boru: (D, t, L, adet) => 0.02466 * (D - t) * t * num(L) / 1000 * num(adet || 1),
  // Köşebent: kg = (2a - t) × t × 0.00785 × L(mm) / 1000
  kosebent: (a, t, L, adet) => 0.00785 * (2 * a - t) * t * num(L) / 1000 * num(adet || 1),
  // Kutu profil: kg = (2(a+b) - 4t) × t × 0.00785 × L(mm) / 1000
  kutu: (a, b, t, L, adet) => 0.00785 * (2 * (a + b) - 4 * t) * t * num(L) / 1000 * num(adet || 1),
};

const ResultCard = ({ result, perMeter }) => (
  <div className="mt-4 p-5 rounded-lg bg-primary/10 border border-primary/30 space-y-2">
    <div className="text-xs uppercase font-semibold text-muted-foreground">Toplam Ağırlık</div>
    <div className="text-4xl font-bold num text-primary" data-testid="calc-total-kg">{round(result, 2).toLocaleString("tr-TR")} kg</div>
    {perMeter !== undefined && (
      <div className="text-sm text-muted-foreground">
        Metre başına: <span className="num font-semibold text-foreground">{round(perMeter, 3)} kg/m</span>
      </div>
    )}
  </div>
);

const FieldGrid = ({ children }) => <div className="grid grid-cols-2 md:grid-cols-3 gap-4">{children}</div>;

export default function IronCalc() {
  return (
    <div className="space-y-6 max-w-4xl" data-testid="iron-calc-page">
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-lg bg-primary/15 flex items-center justify-center text-primary">
          <Calculator size={22} />
        </div>
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight">Demir Ağırlık Hesaplama</h1>
          <p className="text-sm text-muted-foreground">Ölçülerden kilogram ağırlığını anında hesaplayın (Yoğunluk: 7.85 kg/dm³)</p>
        </div>
      </div>

      <Card className="p-5">
        <Tabs defaultValue="yuvarlak">
          <TabsList className="grid grid-cols-4 md:grid-cols-7 mb-6 h-auto">
            <TabsTrigger value="yuvarlak" data-testid="calc-tab-yuvarlak">Yuvarlak</TabsTrigger>
            <TabsTrigger value="kare" data-testid="calc-tab-kare">Kare</TabsTrigger>
            <TabsTrigger value="lama" data-testid="calc-tab-lama">Lama</TabsTrigger>
            <TabsTrigger value="sac" data-testid="calc-tab-sac">Sac</TabsTrigger>
            <TabsTrigger value="boru" data-testid="calc-tab-boru">Boru</TabsTrigger>
            <TabsTrigger value="kosebent" data-testid="calc-tab-kosebent">Köşebent</TabsTrigger>
            <TabsTrigger value="kutu" data-testid="calc-tab-kutu">Kutu Profil</TabsTrigger>
          </TabsList>

          <TabsContent value="yuvarlak"><Yuvarlak /></TabsContent>
          <TabsContent value="kare"><Kare /></TabsContent>
          <TabsContent value="lama"><Lama /></TabsContent>
          <TabsContent value="sac"><Sac /></TabsContent>
          <TabsContent value="boru"><Boru /></TabsContent>
          <TabsContent value="kosebent"><Kosebent /></TabsContent>
          <TabsContent value="kutu"><Kutu /></TabsContent>
        </Tabs>
      </Card>

      <Card className="p-5 bg-muted/30">
        <div className="flex items-start gap-2 text-sm text-muted-foreground">
          <Info size={16} className="mt-0.5 shrink-0" />
          <div>
            Hesaplamalar TS standartlarına göre yapılır. <b>Tüm ölçüler milimetre (mm)</b> cinsindendir — çap, kalınlık, uzunluk, genişlik. Örnek: 6 metre = 6000 mm. Gerçek tartıda %2-3 sapma olabilir.
          </div>
        </div>
      </Card>
    </div>
  );
}

// ============ Individual calculators ============

function Yuvarlak() {
  const [d, setD] = useState(20);
  const [L, setL] = useState(6000);
  const [n, setN] = useState(1);
  const per = 0.006165 * d * d;
  const total = calc.yuvarlak(num(d), num(L), num(n));
  return (
    <div>
      <div className="text-sm text-muted-foreground mb-3">Formül: Ø² × 0.006165 × uzunluk / 1000 (tüm ölçüler mm)</div>
      <FieldGrid>
        <div><Label>Çap (Ø mm)</Label><Input type="number" value={d} onChange={(e) => setD(e.target.value)} data-testid="calc-yuvarlak-cap" /></div>
        <div><Label>Uzunluk (mm)</Label><Input type="number" value={L} onChange={(e) => setL(e.target.value)} /></div>
        <div><Label>Adet</Label><Input type="number" value={n} onChange={(e) => setN(e.target.value)} /></div>
      </FieldGrid>
      <ResultCard result={total} perMeter={per} />
    </div>
  );
}

function Kare() {
  const [a, setA] = useState(20);
  const [L, setL] = useState(6000);
  const [n, setN] = useState(1);
  const per = 0.00785 * a * a;
  return (
    <div>
      <div className="text-sm text-muted-foreground mb-3">Formül: a² × 0.00785 × uzunluk / 1000 (tüm ölçüler mm)</div>
      <FieldGrid>
        <div><Label>Kenar (a mm)</Label><Input type="number" value={a} onChange={(e) => setA(e.target.value)} /></div>
        <div><Label>Uzunluk (mm)</Label><Input type="number" value={L} onChange={(e) => setL(e.target.value)} /></div>
        <div><Label>Adet</Label><Input type="number" value={n} onChange={(e) => setN(e.target.value)} /></div>
      </FieldGrid>
      <ResultCard result={calc.kare(num(a), num(L), num(n))} perMeter={per} />
    </div>
  );
}

function Lama() {
  const [a, setA] = useState(50);
  const [b, setB] = useState(10);
  const [L, setL] = useState(6000);
  const [n, setN] = useState(1);
  const per = 0.00785 * a * b;
  return (
    <div>
      <div className="text-sm text-muted-foreground mb-3">Formül: en × kalınlık × 0.00785 × uzunluk / 1000 (tüm ölçüler mm)</div>
      <FieldGrid>
        <div><Label>En (a mm)</Label><Input type="number" value={a} onChange={(e) => setA(e.target.value)} /></div>
        <div><Label>Kalınlık (b mm)</Label><Input type="number" value={b} onChange={(e) => setB(e.target.value)} /></div>
        <div><Label>Uzunluk (mm)</Label><Input type="number" value={L} onChange={(e) => setL(e.target.value)} /></div>
        <div><Label>Adet</Label><Input type="number" value={n} onChange={(e) => setN(e.target.value)} /></div>
      </FieldGrid>
      <ResultCard result={calc.lama(num(a), num(b), num(L), num(n))} perMeter={per} />
    </div>
  );
}

function Sac() {
  const [L, setL] = useState(3000);
  const [W, setW] = useState(1500);
  const [t, setT] = useState(3);
  const [n, setN] = useState(1);
  const perLevha = num(L) * num(W) * num(t) * DENSITY / 1000000;
  return (
    <div>
      <div className="text-sm text-muted-foreground mb-3">Formül: uzunluk × genişlik × kalınlık × 7.85 / 1.000.000 (tüm ölçüler mm)</div>
      <FieldGrid>
        <div><Label>Uzunluk (mm)</Label><Input type="number" value={L} onChange={(e) => setL(e.target.value)} /></div>
        <div><Label>Genişlik (mm)</Label><Input type="number" value={W} onChange={(e) => setW(e.target.value)} /></div>
        <div><Label>Kalınlık (mm)</Label><Input type="number" step="0.01" value={t} onChange={(e) => setT(e.target.value)} /></div>
        <div><Label>Adet (levha)</Label><Input type="number" value={n} onChange={(e) => setN(e.target.value)} /></div>
      </FieldGrid>
      <div className="mt-4 p-5 rounded-lg bg-primary/10 border border-primary/30 space-y-2">
        <div className="text-xs uppercase font-semibold text-muted-foreground">Toplam Ağırlık</div>
        <div className="text-4xl font-bold num text-primary">{round(calc.sac(num(L), num(W), num(t), num(n)), 2).toLocaleString("tr-TR")} kg</div>
        <div className="text-sm text-muted-foreground">Levha başına: <span className="num font-semibold text-foreground">{round(perLevha, 2)} kg</span></div>
      </div>
    </div>
  );
}

function Boru() {
  const [D, setD] = useState(60);
  const [t, setT] = useState(3);
  const [L, setL] = useState(6000);
  const [n, setN] = useState(1);
  const per = 0.02466 * (num(D) - num(t)) * num(t);
  return (
    <div>
      <div className="text-sm text-muted-foreground mb-3">Formül: (D - t) × t × 0.02466 × uzunluk / 1000 (tüm ölçüler mm)</div>
      <FieldGrid>
        <div><Label>Dış Çap (D mm)</Label><Input type="number" value={D} onChange={(e) => setD(e.target.value)} /></div>
        <div><Label>Et Kalınlığı (t mm)</Label><Input type="number" step="0.1" value={t} onChange={(e) => setT(e.target.value)} /></div>
        <div><Label>Uzunluk (mm)</Label><Input type="number" value={L} onChange={(e) => setL(e.target.value)} /></div>
        <div><Label>Adet</Label><Input type="number" value={n} onChange={(e) => setN(e.target.value)} /></div>
      </FieldGrid>
      <ResultCard result={calc.boru(num(D), num(t), num(L), num(n))} perMeter={per} />
    </div>
  );
}

function Kosebent() {
  const [a, setA] = useState(40);
  const [t, setT] = useState(4);
  const [L, setL] = useState(6000);
  const [n, setN] = useState(1);
  const per = 0.00785 * (2 * num(a) - num(t)) * num(t);
  return (
    <div>
      <div className="text-sm text-muted-foreground mb-3">Eşit kollu köşebent · Formül: (2a - t) × t × 0.00785 × uzunluk / 1000 (tüm ölçüler mm)</div>
      <FieldGrid>
        <div><Label>Kanat (a mm)</Label><Input type="number" value={a} onChange={(e) => setA(e.target.value)} /></div>
        <div><Label>Kalınlık (t mm)</Label><Input type="number" step="0.1" value={t} onChange={(e) => setT(e.target.value)} /></div>
        <div><Label>Uzunluk (mm)</Label><Input type="number" value={L} onChange={(e) => setL(e.target.value)} /></div>
        <div><Label>Adet</Label><Input type="number" value={n} onChange={(e) => setN(e.target.value)} /></div>
      </FieldGrid>
      <ResultCard result={calc.kosebent(num(a), num(t), num(L), num(n))} perMeter={per} />
    </div>
  );
}

function Kutu() {
  const [a, setA] = useState(40);
  const [b, setB] = useState(40);
  const [t, setT] = useState(2);
  const [L, setL] = useState(6000);
  const [n, setN] = useState(1);
  const per = 0.00785 * (2 * (num(a) + num(b)) - 4 * num(t)) * num(t);
  return (
    <div>
      <div className="text-sm text-muted-foreground mb-3">Kare / Dikdörtgen kutu profil · Formül: (2(a+b) - 4t) × t × 0.00785 × uzunluk / 1000 (tüm ölçüler mm)</div>
      <FieldGrid>
        <div><Label>En (a mm)</Label><Input type="number" value={a} onChange={(e) => setA(e.target.value)} /></div>
        <div><Label>Boy (b mm)</Label><Input type="number" value={b} onChange={(e) => setB(e.target.value)} /></div>
        <div><Label>Kalınlık (t mm)</Label><Input type="number" step="0.1" value={t} onChange={(e) => setT(e.target.value)} /></div>
        <div><Label>Uzunluk (mm)</Label><Input type="number" value={L} onChange={(e) => setL(e.target.value)} /></div>
        <div><Label>Adet</Label><Input type="number" value={n} onChange={(e) => setN(e.target.value)} /></div>
      </FieldGrid>
      <ResultCard result={calc.kutu(num(a), num(b), num(t), num(L), num(n))} perMeter={per} />
    </div>
  );
}
